<template>
  <div class="sena-settlement">
    <va-card :title="cardTitle">
      <template slot="actions">
        <va-button color="gray" icon="fa fa-refresh" @click="refetchAll"/>
      </template>

      <div v-if="!editionRow" class="sena-empty">{{ $t('sena.empty.loading') }}</div>

      <template v-else>
        <!-- Já fechada: só leitura. -->
        <va-alert v-if="editionRow.settled_at" color="info" class="mb-3">
          {{ $t('sena.hints.alreadySettled', {
            at: $moment(editionRow.settled_at).format('DD/MM/YYYY [às] HH:mm'),
          }) }}
        </va-alert>

        <!-- CHECKLIST. Cada item é uma condição que fn_apurar_sena_edition
             valida no banco; mostrar aqui evita descobrir o erro só no commit. -->
        <div class="sena-checklist mb-4">
          <div v-for="c in checklist" :key="c.key" class="sena-checklist__row">
            <span class="sena-checklist__mark" :class="c.ok ? 'ok' : 'bad'">
              {{ c.ok ? '✓' : '✕' }}
            </span>
            <span>{{ c.label }}</span>
          </div>
        </div>

        <va-alert v-if="preview && preview.will_void" color="danger" class="mb-3">
          {{ preview.void_reason }}
          <br/>
          <small>{{ $t('sena.hints.voidConsequence') }}</small>
        </va-alert>

        <!-- Números do preview. -->
        <div class="sena-summary mb-4" v-if="preview">
          <span class="sena-summary__item">
            <b>{{ preview.games_resolved }}/{{ preview.games_total }}</b>
            {{ $t('sena.labels.gamesResolved') }}
          </span>
          <span class="sena-summary__item">
            <b>{{ preview.tickets_total }}</b> {{ $t('sena.labels.tickets') }}
          </span>
          <span class="sena-summary__item">
            <b>{{ preview.tickets_alive }}</b> {{ $t('sena.labels.survivors') }}
          </span>
          <span class="sena-summary__item">
            <b>{{ preview.winners_count }}</b> {{ $t('sena.labels.winners') }}
            ({{ $t('sena.labels.prizeTier6') }})
          </span>
          <span class="sena-summary__item" v-if="preview.prize_per_winner">
            <b>{{ money(preview.prize_per_winner) }}</b> {{ $t('sena.labels.perWinner') }}
          </span>
          <span class="sena-summary__item">
            <b>{{ preview.winners_count_5 }}</b> {{ $t('sena.labels.winners5') }}
          </span>
          <span class="sena-summary__item" v-if="preview.prize_per_winner_5">
            <b>{{ money(preview.prize_per_winner_5) }}</b> {{ $t('sena.labels.perWinner5') }}
          </span>
        </div>

        <!-- Histograma de acertos. -->
        <h6 class="mb-2">{{ $t('sena.cards.histogram') }}</h6>
        <table class="sena-hist mb-4" v-if="preview">
          <tbody>
            <tr v-for="h in histogram" :key="h.hits">
              <td class="sena-hist__label">{{ h.hits }} {{ $t('sena.labels.hitsShort') }}</td>
              <td class="sena-hist__bar">
                <div class="sena-hist__fill" :style="{ width: h.pct + '%' }"/>
              </td>
              <td class="sena-hist__count">{{ h.count }}</td>
            </tr>
          </tbody>
        </table>

        <!-- COMMIT. Exige digitar o nome da edição: é irreversível
             (fn_apurar_sena_edition levanta SENA_ALREADY_SETTLED na segunda vez). -->
        <div v-if="!editionRow.settled_at">
          <hr/>
          <h6 class="mt-3">{{ $t('sena.cards.commit') }}</h6>
          <p class="sena-hint">{{ $t('sena.hints.commitIrreversible', { name: editionRow.name }) }}</p>
          <va-input
            v-model="confirmName"
            :label="$t('sena.forms.confirmNameLabel')"
            :placeholder="editionRow.name"
            :success="nameMatches"
          />
          <va-button
            class="mt-2"
            :color="preview && preview.will_void ? 'danger' : 'success'"
            :disabled="!canCommit"
            @click="settle">
            {{ commitLabel }}
          </va-button>
        </div>
      </template>
    </va-card>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { senaErrorMixin } from './senaErrors'

const PREVIEW = gql`
  query SenaPreview($edition: Int!) {
    editionRow: sena_edition_by_pk(id: $edition) {
      id name edition_number status prize_amount prize_amount_5 lockout_at
      settled_at total_tickets cancel_reason
      total_winners prize_per_winner
      total_winners_5 prize_per_winner_5
    }
    preview: fn_sena_preview_edition(args: {p_edition_id: $edition}) {
      edition_id
      will_void
      void_reason
      games_total
      games_played
      games_blocked
      games_resolved
      games_missing_result
      tickets_total
      tickets_alive
      winners_count
      prize_per_winner
      winners_count_5
      prize_per_winner_5
      hits_0 hits_1 hits_2 hits_3 hits_4 hits_5 hits_6
    }
    scheduled: sena_edition_game_aggregate(
      where: {sena_edition_id: {_eq: $edition}, status: {_eq: SCHEDULED}}
    ) {
      aggregate { count }
    }
  }
`

export default {
  name: 'SenaEditionSettlement',
  mixins: [senaErrorMixin],
  props: {
    edition: { required: true },
  },
  data () {
    return {
      editionRow: null,
      previewRows: [],
      scheduledCount: 0,
      confirmName: '',
      busy: false,
    }
  },
  apollo: {
    previewData: {
      query: PREVIEW,
      variables () {
        return { edition: Number(this.edition) }
      },
      manual: true,
      fetchPolicy: 'network-only',
      result ({ data, loading }) {
        if (loading || !data) return
        this.editionRow = data.editionRow
        this.previewRows = data.preview || []
        this.scheduledCount = data.scheduled.aggregate.count
      },
    },
  },
  computed: {
    preview () {
      return this.previewRows.length ? this.previewRows[0] : null
    },
    cardTitle () {
      return this.editionRow
        ? `${this.$t('sena.cards.settlement')} — ${this.editionRow.name}`
        : this.$t('sena.cards.settlement')
    },
    // Espelha as validações de fn_apurar_sena_edition, na mesma ordem.
    checklist () {
      const p = this.preview
      const e = this.editionRow
      if (!p || !e) return []
      return [
        {
          key: 'status',
          ok: ['OPEN', 'LOCKED'].includes(e.status),
          label: this.$t('sena.checklist.status', { status: this.$t('sena.status.' + e.status) }),
        },
        {
          key: 'settled',
          ok: !e.settled_at,
          label: this.$t('sena.checklist.notSettled'),
        },
        {
          key: 'six',
          ok: p.games_total === 6,
          label: this.$t('sena.checklist.sixGames', { n: p.games_total }),
        },
        {
          key: 'reviewed',
          ok: this.scheduledCount === 0,
          label: this.$t('sena.checklist.reviewed', { n: this.scheduledCount }),
        },
        {
          // Rodada anulada não exige placar: o caminho de anulação sai antes.
          key: 'results',
          ok: p.will_void || p.games_missing_result === 0,
          label: this.$t('sena.checklist.results', { n: p.games_missing_result }),
        },
      ]
    },
    histogram () {
      const p = this.preview
      if (!p) return []
      const rows = [0, 1, 2, 3, 4, 5, 6].map(h => ({ hits: h, count: p[`hits_${h}`] || 0 }))
      const max = Math.max(1, ...rows.map(r => r.count))
      return rows.map(r => ({ ...r, pct: Math.round((r.count / max) * 100) }))
    },
    nameMatches () {
      return !!this.editionRow &&
        this.confirmName.trim().toLowerCase() === this.editionRow.name.trim().toLowerCase()
    },
    canCommit () {
      return !this.busy &&
        this.nameMatches &&
        this.checklist.length > 0 &&
        this.checklist.every(c => c.ok)
    },
    commitLabel () {
      if (this.busy) return this.$t('forms.submitting')
      return this.preview && this.preview.will_void
        ? this.$t('sena.buttons.commitVoid')
        : this.$t('sena.buttons.commit')
    },
  },
  methods: {
    money (v) {
      return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    refetchAll () {
      this.$apollo.queries.previewData.refetch()
    },
    settle () {
      this.busy = true
      this.$apollo
        .mutate({
          mutation: gql`
            mutation ApurarSena($edition: Int!) {
              fn_apurar_sena_edition(args: {p_edition_id: $edition}) {
                edition_id
                will_void
                void_reason
                winners_count
                prize_per_winner
                winners_count_5
                prize_per_winner_5
                tickets_total
              }
            }
          `,
          variables: { edition: Number(this.edition) },
        })
        .then(({ data }) => {
          this.busy = false
          this.confirmName = ''
          const row = data.fn_apurar_sena_edition[0]
          this.senaToastSuccess(row.will_void ? 'sena.toasts.voided' : 'sena.toasts.settled')
          this.refetchAll()
        })
        .catch(error => {
          this.busy = false
          this.senaToastError(error, 'toasts.errorUpdate')
        })
    },
  },
}
</script>

<style lang="scss">
.sena-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  padding: 0.75rem 1rem;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
}

.sena-checklist {
  &__row {
    padding: 0.3rem 0;
  }

  &__mark {
    display: inline-block;
    width: 1.5rem;
    font-weight: bold;

    &.ok { color: #4caf50; }
    &.bad { color: #e53935; }
  }
}

.sena-hist {
  width: 100%;
  max-width: 520px;

  &__label { width: 90px; }

  &__count {
    width: 60px;
    text-align: right;
  }

  &__bar {
    padding: 0.2rem 0.5rem;
  }

  &__fill {
    height: 14px;
    background: #a38e47;
    border-radius: 2px;
    min-width: 2px;
  }
}

.sena-empty {
  padding: 2.5rem 1rem;
  text-align: center;
  color: #888888;
}

.sena-hint {
  font-size: $font-size-mini;
  color: #888888;
}
</style>
